import { useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useLocalStore } from "./use-local-store";
import { isDiscontinued, DISCONTINUED_TITLE } from "@/lib/discontinued-products";
import { mark, measureFromStart } from "@/lib/user-timing";

let firstAddTimed = false;

export type CartItem = {
  productId: string;
  psId: number | null;
  name: string;
  price: number;
  imageUrl: string | null;
  quantity: number;
  originalPrice?: number | null;
  /** Διαθέσιμο απόθεμα· αν οριστεί (>0) η ποσότητα δεν το ξεπερνά. */
  stock?: number | null;
  /** Αν προστέθηκε ως μέρος πακέτου MrBox: το id του πακέτου. */
  bundleId?: string | null;
  /**
   * Μοναδικό κλειδί αντιτύπου πακέτου: επιτρέπει να υπάρχουν στο καλάθι
   * πολλά αντίτυπα του ΙΔΙΟΥ πακέτου, το καθένα με δική του έκπτωση.
   */
  bundleKey?: string | null;
  /** Ποσοστό έκπτωσης πακέτου (0–1) — μόνο για εμφάνιση στο UI. */
  bundlePct?: number | null;
  /** Τίτλος πακέτου, για εμφάνιση στο καλάθι. */
  bundleTitle?: string | null;
  /** Εγγυοδοσία φιάλης ανά τεμάχιο (με ΦΠΑ) — περιλαμβάνεται ήδη στο price. */
  deposit?: number | null;
  /** Συντελεστής ΦΠΑ (%) του προϊόντος — για ανάλυση τιμολογίου. */
  taxRate?: number | null;
  /** Mystery box: το προϊόν δεν εμφανίζεται καθόλου στον πελάτη. */
  hidden?: boolean;
  /** Mystery box: εναλλακτικό όνομα εμφάνισης (π.χ. «Mystery Box — Τρόφιμα»). */
  displayName?: string | null;
  /** Mystery box: συνολική αξία πακέτου που εμφανίζεται στη μία γραμμή. */
  mysteryTotal?: number | null;
};

/** Γραμμές που βλέπει ο πελάτης (τα κρυφά προϊόντα mystery box εξαιρούνται). */
export const visibleCartItems = (items: CartItem[]) => items.filter((i) => !i.hidden);

/** Είναι η γραμμή «κρυμμένο» πακέτο-έκπληξη; */
export const isMysteryLine = (i: CartItem) => i.mysteryTotal != null;

export const cartItemLabel = (i: CartItem) => i.displayName || i.name;

/** Σύνολο γραμμής — για mystery box είναι η αξία ολόκληρου του πακέτου. */
export const cartLineTotal = (i: CartItem) => i.mysteryTotal ?? i.price * i.quantity;

/** Κλειδί αντιτύπου πακέτου (παλιά καλάθια δεν έχουν bundleKey). */
export const lineBundleKey = (i: CartItem) => i.bundleKey ?? i.bundleId ?? null;

export type CartBundleGroup = {
  kind: "bundle";
  key: string;
  bundleId: string;
  title: string;
  pct: number;
  /** Ορατές γραμμές του πακέτου (τα mystery προϊόντα μένουν κρυφά). */
  items: CartItem[];
  /** Όλες οι γραμμές του πακέτου, μαζί με τις κρυφές. */
  allItems: CartItem[];
  total: number;
  mystery: CartItem | null;
};

export type CartRow = { kind: "item"; item: CartItem } | CartBundleGroup;

/**
 * Ομαδοποιεί τις γραμμές του καλαθιού: τα προϊόντα κάθε πακέτου MrBox
 * εμφανίζονται μαζί, κάτω από τον τίτλο του πακέτου.
 */
export function groupCartRows(items: CartItem[]): CartRow[] {
  const rows: CartRow[] = [];
  const byKey = new Map<string, CartBundleGroup>();
  for (const it of items) {
    const key = it.bundleId ? lineBundleKey(it) : null;
    if (!key) {
      if (it.hidden) continue;
      rows.push({ kind: "item", item: it });
      continue;
    }
    let g = byKey.get(key);
    if (!g) {
      g = {
        kind: "bundle",
        key,
        bundleId: it.bundleId!,
        title: it.bundleTitle ?? "MrBox",
        pct: it.bundlePct ?? 0,
        items: [],
        allItems: [],
        total: 0,
        mystery: null,
      };
      byKey.set(key, g);
      rows.push(g);
    }
    g.allItems.push(it);
    if (isMysteryLine(it)) g.mystery = it;
    if (!it.hidden) g.items.push(it);
    // Το mysteryTotal της ορατής γραμμής είναι ήδη η αξία ΟΛΟΥ του Mystery Box.
    // Στην ομαδοποιημένη κάρτα αθροίζουμε τις πραγματικές γραμμές μία φορά,
    // αλλιώς η αξία των κρυφών προϊόντων προστίθεται δεύτερη φορά.
    g.total += it.price * it.quantity;
  }
  return rows;
}



const KEY = "mrpanda.cart.v1";

/**
 * Εκπέμπεται κάθε φορά που κάτι μπάινει πραγματικά στο καλάθι, ώστε οι
 * σελίδες (π.χ. το Pandaki) να μπορούν να ρωτήσουν τον πελάτη αν θέλει
 * να ξεκινήσει νέα αναζήτηση.
 */
export const CART_ADD_EVENT = "mrpanda:cart-add";


/** Μέγιστη επιτρεπτή ποσότητα ανά προϊόν (συμβαδίζει με τον έλεγχο στον server). */
export const MAX_QTY_PER_ITEM = 999;

function maxQtyMessage(name: string) {
  return `Το «${name}» έφτασε το όριο των ${MAX_QTY_PER_ITEM} τεμαχίων — σχεδόν 1.000! 🐼🎉`;
}

/**
 * Περιορίζει την ποσότητα στο διαθέσιμο απόθεμα (αν είναι γνωστό) και πάντα
 * στο ανώτατο όριο ανά προϊόν, ώστε να μην ξεπεραστεί ποτέ πριν το checkout.
 */
function clampToStock(
  qty: number,
  stock: number | null | undefined,
): { qty: number; cappedByMax: boolean } {
  let q = Math.max(1, Math.floor(qty));
  if (typeof stock === "number" && stock > 0) q = Math.min(q, Math.floor(stock));
  const cappedByMax = q > MAX_QTY_PER_ITEM;
  if (cappedByMax) q = MAX_QTY_PER_ITEM;
  return { qty: q, cappedByMax };
}

export function useCart() {
  const [items, setItems] = useLocalStore<CartItem[]>(KEY, []);
  const toastedRef = useRef<Set<string>>(new Set());

  const add = useCallback(
    (item: Omit<CartItem, "quantity">, qty = 1) => {
      if (isDiscontinued(item.psId, item.name)) {
        toast(DISCONTINUED_TITLE, {
          description:
            "Οι πολυσυσκευασίες εμφιαλωμένου νερού 6x1,5lt · 24x500ml · 12x500ml δεν διατίθενται πλέον.",
          duration: 6000,
        });
        return;
      }
      setItems((prev) => {
        // Οι γραμμές πακέτου (MrBox/Mystery) ΔΕΝ συγχωνεύονται ποτέ με άλλες
        // γραμμές: αλλιώς μια δεύτερη προσθήκη ίδιου πακέτου (ή προϊόν που
        // υπάρχει ήδη στο καλάθι) θα κρατούσε παλιά αξία πακέτου και ο
        // πελάτης θα χρεωνόταν κρυφά προϊόντα.
        const idx =
          item.bundleId != null
            ? -1
            : prev.findIndex((p) => p.productId === item.productId && !p.bundleId);
        if (idx >= 0) {
          const next = [...prev];
          const existing = next[idx];
          const stock = item.stock ?? existing.stock ?? null;
          const { qty: newQty, cappedByMax } = clampToStock(
            existing.quantity + qty,
            stock,
          );
          if (
            cappedByMax &&
            existing.quantity < MAX_QTY_PER_ITEM &&
            !toastedRef.current.has(existing.productId)
          ) {
            toastedRef.current.add(existing.productId);
            toast(maxQtyMessage(existing.name));
          }
          next[idx] = {
            ...existing,
            stock: item.stock != null ? item.stock : existing.stock ?? null,
            quantity: newQty,
          };
          return next;
        }
        const { qty: newQty, cappedByMax } = clampToStock(qty, item.stock);
        if (cappedByMax && !toastedRef.current.has(item.productId)) {
          toastedRef.current.add(item.productId);
          toast(maxQtyMessage(item.name));
        }
        return [...prev, { ...item, quantity: newQty }];
      });
      window.dispatchEvent(
        new CustomEvent(CART_ADD_EVENT, {
          detail: { productId: item.productId, name: item.name },
        }),
      );
      // User Timing: κρίσιμη εμπειρία χρήστη «προσθήκη στο καλάθι».
      mark("cart:add-to-cart");
      if (!firstAddTimed) {
        firstAddTimed = true;
        measureFromStart("cart:time-to-first-add");
      }
    },
    [setItems],
  );

  /**
   * Αφαίρεση προϊόντος. Αν ανήκει σε πακέτο MrBox, αφαιρείται ΟΛΟ το πακέτο
   * (και μαζί η έκπτωσή του) — ένα πακέτο δεν στέκει «μισό».
   */
  const remove = useCallback(
    (productId: string, bundleKeyOrId?: string | null) =>
      setItems((prev) => {
        if (bundleKeyOrId) {
          const match = (p: CartItem) =>
            !!p.bundleId &&
            (lineBundleKey(p) === bundleKeyOrId || p.bundleId === bundleKeyOrId);
          if (prev.some(match)) return prev.filter((p) => !match(p));
        }
        // Προτεραιότητα σε απλή γραμμή: ένα προϊόν που υπάρχει και μόνο του
        // και μέσα σε πακέτο δεν πρέπει να διαλύει το πακέτο.
        const target =
          prev.find((p) => p.productId === productId && !p.bundleId) ??
          prev.find((p) => p.productId === productId);
        if (target?.bundleId) {
          const key = lineBundleKey(target);
          const removed = prev.filter((p) => lineBundleKey(p) === key);
          if (removed.length > 1) {
            toast(`Το πακέτο «${target.bundleTitle ?? "MrBox"}» αφαιρέθηκε ολόκληρο`, {
              description: "Τα πακέτα MrBox ισχύουν μόνο ολόκληρα, μαζί με την έκπτωσή τους.",
            });
          }
          return prev.filter((p) => lineBundleKey(p) !== key);
        }
        return prev.filter((p) => !(p.productId === productId && !p.bundleId));
      }),
    [setItems],
  );

  /** Αφαίρεση ενός συγκεκριμένου αντιτύπου πακέτου. */
  const removeBundle = useCallback(
    (key: string) =>
      setItems((prev) => prev.filter((p) => !p.bundleId || lineBundleKey(p) !== key)),
    [setItems],
  );

  /**
   * Προσθήκη ενός ακόμη αντιτύπου του ίδιου πακέτου: αντιγράφουμε όλες τις
   * γραμμές του με νέο bundleKey, ώστε κάθε αντίτυπο να κρατά τη δική του
   * έκπτωση (ο server ομαδοποιεί επίσης ανά αντίτυπο).
   */
  const duplicateBundle = useCallback(
    (key: string) =>
      setItems((prev) => {
        const lines = prev.filter((p) => p.bundleId && lineBundleKey(p) === key);
        if (lines.length === 0) return prev;
        const newKey = `${lines[0].bundleId}#${Math.random().toString(36).slice(2, 10)}`;
        const copies = lines.map((p) => ({ ...p, bundleKey: newKey }));
        const lastIdx = prev.lastIndexOf(lines[lines.length - 1]);
        return [...prev.slice(0, lastIdx + 1), ...copies, ...prev.slice(lastIdx + 1)];
      }),
    [setItems],
  );


  const setQty = useCallback(
    (productId: string, quantity: number) => {
      // Μηδενική ποσότητα = αφαίρεση (και ολόκληρο πακέτο, αν ανήκει σε MrBox).
      if (quantity <= 0) return remove(productId);
      return setItems((prev) =>
        prev
          .map((p) => {
            // Οι ποσότητες γραμμών πακέτου αλλάζουν μόνο μαζί με το πακέτο.
            if (p.productId !== productId || p.bundleId) return p;
            const { qty: newQty, cappedByMax } = clampToStock(
              quantity,
              p.stock,
            );
            if (
              cappedByMax &&
              p.quantity < MAX_QTY_PER_ITEM &&
              !toastedRef.current.has(p.productId)
            ) {
              toastedRef.current.add(p.productId);
              toast(maxQtyMessage(p.name));
            }
            return { ...p, quantity: newQty };
          })
          .filter((p) => p.quantity > 0),
      );
    },
    [setItems, remove],
  );

  /**
   * Συγχρονίζει τις τιμές του καλαθιού με τις επίσημες τιμές του server.
   * Το καλάθι ζει τοπικά, οπότε μπορεί να κρατά παλιές τιμές — χωρίς αυτό ο
   * πελάτης έβλεπε ένα ποσό στο ταμείο και άλλο στην πληρωμή.
   * Επιστρέφει true αν κάποια τιμή άλλαξε.
   */
  const syncPrices = useCallback(
    (prices: Record<string, number>) => {
      let changed = false;
      setItems((prev) => {
        const next = prev.map((p) => {
          const fresh = prices[p.productId];
          if (typeof fresh !== "number" || !Number.isFinite(fresh)) return p;
          if (Math.abs(fresh - p.price) < 0.005) return p;
          changed = true;
          return { ...p, price: fresh };
        });
        if (!changed) return prev;
        // Η αξία που εμφανίζεται στα Mystery Box ακολουθεί τις νέες τιμές.
        const totals = new Map<string, number>();
        for (const p of next) {
          const key = p.bundleId ? lineBundleKey(p) : null;
          if (key) totals.set(key, (totals.get(key) ?? 0) + p.price * p.quantity);
        }
        return next.map((p) =>
          p.mysteryTotal != null && p.bundleId
            ? { ...p, mysteryTotal: totals.get(lineBundleKey(p)!) ?? p.mysteryTotal }
            : p,
        );
      });
      return changed;
    },
    [setItems],
  );

  const clear = useCallback(() => setItems([]), [setItems]);

  const replace = useCallback(
    (next: CartItem[]) => setItems(next),
    [setItems],
  );

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, p) => s + p.price * p.quantity, 0);
    const count = items.reduce((s, p) => s + p.quantity, 0);
    const shipping = subtotal > 0 && subtotal < 50 ? 3.5 : 0;
    const depositTotal = items.reduce(
      (s, p) => s + (Number(p.deposit) || 0) * p.quantity,
      0,
    );
    return { subtotal, count, shipping, depositTotal, total: subtotal + shipping };
  }, [items]);

  return {
    items,
    add,
    remove,
    removeBundle,
    duplicateBundle,
    setQty,
    syncPrices,
    clear,
    replace,
    ...totals,
  };
}